import React, { useCallback, useEffect, useState } from "react";
import { Container } from "reactstrap";
import Swal from "sweetalert2";
import {
  FiCheck,
  FiEdit2,
  FiEye,
  FiMessageSquare,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import { Bell } from "assets";
import Table from "components/Table";
import TableLoading from "components/TableLoading";
import Pagination from "components/Pagination";
import CustomButton from "components/CustomButton";
import StatusBadge from "components/StatusBadge";
import { FormSelects } from "components/FormInputSelect";
import { TOOLBAR_SELECT_STYLES } from "components/toolbarSelectStyles";
import { columnLayout, actionColumnLayout } from "components/tableColumnLayout";
import { API_DELETE_MAMBOO, API_GET_MAMBOO, API_PUT_MAMBOO } from "services";
import useLoginStore from "hooks/useLoginStore";
import { toDisplayDate } from "utils/date";
import AddLagu from "./AddLagu";
import Detail from "./Detail";
import "./katalogLagu.scss";

/** Sesuai src/helper/songApproval.ts di backend. */
const PENDING = "pending";
const DECLINED = "declined";

const STATUS_LABEL = {
  [PENDING]: "Menunggu Persetujuan",
  [DECLINED]: "Ditolak",
};

const BADGE_FIT = { flex: "0 0 auto", alignSelf: "auto", width: "fit-content" };

const SHOW_OPTIONS = [5, 10, 25, 50].map((n) => ({
  label: String(n),
  value: n,
}));

/**
 * Halaman "Register Req" — permintaan pendaftaran lagu.
 *
 * Satu komponen, dua sudut pandang, karena datanya memang sama:
 *
 *  - PENCIPTA  mendaftarkan lagu dari sini. Lagunya masuk sebagai permintaan
 *              (status pending) dan belum tampil di Katalog Lagu. Yang ditolak
 *              tampil bersama catatan publisher dan bisa direvisi.
 *  - PUBLISHER melihat pengajuan yang masuk dan memutuskannya. Begitu
 *              disetujui, lagunya pindah sendiri ke Katalog Lagu — keduanya
 *              menyaring kolom `approval_status` yang sama.
 *
 * Kewenangan menyetujui ditegakkan backend (`canReview`); gating di sini hanya
 * mencegah salah klik.
 */
const RegisterReq = () => {
  const serverProfile = useLoginStore((state) => state.serverProfile);
  const roleCode = serverProfile?.role_code;
  const isPencipta = roleCode === 3;
  const canReview =
    roleCode === 1 ||
    (roleCode === 2 &&
      serverProfile?.access?.some((a) => a.access_name === "Super Admin"));

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(SHOW_OPTIONS[1]);
  const [isLoading, setIsLoading] = useState(true);
  const [reviewingId, setReviewingId] = useState(null);
  const [formMode, setFormMode] = useState("");
  const [detailData, setDetailData] = useState(null);
  const [detailRow, setDetailRow] = useState(null);

  const fetchRequests = useCallback(() => {
    setIsLoading(true);
    API_GET_MAMBOO.dynamic
      .getSongRequests(page, limit.value)
      .then((resp) => {
        setRows(resp?.data || []);
        setTotal(resp?.total || 0);
      })
      .catch(() => {
        setRows([]);
        setTotal(0);
      })
      .finally(() => setIsLoading(false));
  }, [page, limit]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  /**
   * Revisi oleh pencipta atas lagu yang ditolak.
   *
   * Memakai formulir edit yang sama dengan publisher. Begitu revisinya
   * disimpan, backend mengembalikan statusnya ke `pending` dan membersihkan
   * catatan penolakan (lihat update_song di controllers/song.ts) — jadi
   * permintaannya masuk lagi ke daftar publisher.
   */
  const handleRevise = async (row) => {
    setReviewingId(row.song_id);
    try {
      const detail = await API_GET_MAMBOO.dynamic.getSongDetail(row.song_id);
      detail.lisence = (detail.lisence || []).sort(
        (a, b) => Number(a.creator_code) - Number(b.creator_code)
      );
      setDetailData(detail);
      setDetailRow(row);
      setFormMode("revisiLagu");
    } catch (err) {
      Swal.fire(
        "Gagal",
        err?.response?.data?.data || "Data lagu gagal dimuat. Coba lagi.",
        "error"
      );
    } finally {
      setReviewingId(null);
    }
  };

  /**
   * Buka detail lagu sebelum diputuskan.
   *
   * Sama seperti handleRevise, tapi read-only: publisher hanya melihat.
   * Keputusannya diambil dari tombol di halaman detail itu, atau tetap bisa
   * langsung dari daftar.
   */
  const handleViewDetail = async (row) => {
    setReviewingId(row.song_id);
    try {
      const detail = await API_GET_MAMBOO.dynamic.getSongDetail(row.song_id);
      detail.lisence = (detail.lisence || []).sort(
        (a, b) => Number(a.creator_code) - Number(b.creator_code)
      );
      setDetailData(detail);
      setDetailRow(row);
      setFormMode("detailLagu");
    } catch (err) {
      Swal.fire(
        "Gagal",
        err?.response?.data?.data || "Data lagu gagal dimuat. Coba lagi.",
        "error"
      );
    } finally {
      setReviewingId(null);
    }
  };

  const closeForm = () => {
    setFormMode("");
    setDetailData(null);
    setDetailRow(null);
    fetchRequests();
  };

  /**
   * Catatan penolakan ditampilkan di dialog, bukan di dalam sel tabel:
   * publisher bebas menulis sepanjang apa pun dan itu merusak tinggi baris.
   *
   * Dipakai `text`, bukan `html` — catatan adalah teks bebas dari publisher,
   * dan SweetAlert2 menyisipkan `html` apa adanya.
   */
  const showNote = (row) => {
    Swal.fire({
      icon: "info",
      title: "Catatan Penolakan",
      text: row.decline_note,
      confirmButtonText: "Tutup",
    });
  };

  /**
   * Pencipta membatalkan pengajuannya.
   *
   * Hanya untuk pengajuan yang belum disetujui — backend memeriksa hal yang
   * sama (delete_song), jadi tombol yang hilang di sini bukan satu-satunya
   * pengaman.
   */
  const handleDelete = (row) => {
    Swal.fire({
      icon: "warning",
      title: "Batalkan Pengajuan",
      text: `Hapus pengajuan '${row.song_title}'? Tindakan ini tidak dapat dibatalkan.`,
      showCancelButton: true,
      confirmButtonText: "Ya, hapus",
      cancelButtonText: "Batal",
      confirmButtonColor: "var(--danger)",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      setReviewingId(row.song_id);
      try {
        await API_DELETE_MAMBOO.dynamic.deleteSong(row.song_id);
        Swal.fire("Pengajuan dihapus", "", "success");
        fetchRequests();
      } catch (err) {
        Swal.fire(
          "Gagal",
          err?.response?.data?.data || "Pengajuan gagal dihapus. Coba lagi.",
          "error"
        );
      } finally {
        setReviewingId(null);
      }
    });
  };

  const handleApprove = (row, afterSuccess) => {
    Swal.fire({
      icon: "question",
      title: "Setujui Lagu",
      text: `Setujui '${row.song_title}' untuk dimasukkan ke Katalog Lagu?`,
      showCancelButton: true,
      confirmButtonText: "Ya, setujui",
      cancelButtonText: "Batal",
      confirmButtonColor: "var(--success)",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      setReviewingId(row.song_id);
      try {
        await API_PUT_MAMBOO.update.reviewSong(row.song_id, true);
        Swal.fire(
          "Lagu disetujui",
          "Lagu telah dimasukkan ke Katalog Lagu.",
          "success"
        );
        if (afterSuccess) afterSuccess();
        else fetchRequests();
      } catch (err) {
        Swal.fire(
          "Gagal",
          err?.response?.data?.data || "Persetujuan gagal. Coba lagi.",
          "error"
        );
      } finally {
        setReviewingId(null);
      }
    });
  };

  const handleDecline = (row, afterSuccess) => {
    Swal.fire({
      icon: "warning",
      title: "Tolak Lagu",
      text: `Tuliskan alasan penolakan '${row.song_title}'. Alasan ini akan dibaca pencipta pada saat merevisi pengajuannya.`,
      input: "textarea",
      inputPlaceholder: "Contoh: pembagian hak mekanikal belum 100%",
      showCancelButton: true,
      confirmButtonText: "Tolak",
      cancelButtonText: "Batal",
      confirmButtonColor: "var(--danger)",
      // Backend juga menolak alasan kosong; divalidasi di sini supaya
      // pengguna tidak perlu menunggu perjalanan ke server.
      inputValidator: (value) =>
        !value || !value.trim() ? "Alasan penolakan wajib diisi" : undefined,
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      setReviewingId(row.song_id);
      try {
        await API_PUT_MAMBOO.update.reviewSong(
          row.song_id,
          false,
          result.value.trim()
        );
        Swal.fire(
          "Lagu ditolak",
          "Songwriter dapat merevisi dan mengajukannya kembali.",
          "success"
        );
        if (afterSuccess) afterSuccess();
        else fetchRequests();
      } catch (err) {
        Swal.fire(
          "Gagal",
          err?.response?.data?.data || "Penolakan gagal. Coba lagi.",
          "error"
        );
      } finally {
        setReviewingId(null);
      }
    });
  };

  const statusFormatter = (cell) => (
    <StatusBadge
      title={STATUS_LABEL[cell] || cell}
      customStyle={{
        ...BADGE_FIT,
        background: "var(--surface-2)",
        border: `1px solid ${
          cell === DECLINED ? "var(--danger-border)" : "var(--warning-border)"
        }`,
        color: cell === DECLINED ? "var(--danger)" : "#BF6919",
      }}
    />
  );

  const noteFormatter = (cell, row) =>
    cell ? (
      <button
        type="button"
        className="lagu-note-btn"
        title="Lihat catatan penolakan"
        aria-label={`Lihat catatan penolakan untuk ${row.song_title}`}
        onClick={() => showNote(row)}
      >
        <FiMessageSquare />
      </button>
    ) : (
      <span style={{ color: "var(--text-muted)" }}>-</span>
    );

  /**
   * Tombol aksi berupa ikon: teksnya panjang dan kolomnya jadi sempit, sedangkan
   * warnanya sudah membedakan maksudnya. `title` + `aria-label` tetap ada supaya
   * maksudnya terbaca saat disentuh maupun oleh pembaca layar.
   */
  const IconAction = ({ icon: Icon, label, variant, disabled, onClick }) => (
    <button
      type="button"
      className={`lagu-action-btn is-${variant}`}
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon />
    </button>
  );

  const actionFormatter = (cell, row) => {
    const busy = reviewingId === row.song_id;

    if (isPencipta) {
      return (
        <div className="lagu-row-actions">
          {/* Revisi hanya untuk yang sudah ditolak; yang masih pending
              dibiarkan utuh supaya tidak berubah saat publisher menilainya. */}
          {row.approval_status === DECLINED && (
            <IconAction
              icon={FiEdit2}
              label="Revisi lagu"
              variant="edit"
              disabled={busy}
              onClick={() => handleRevise(row)}
            />
          )}
          <IconAction
            icon={FiTrash2}
            label="Batalkan pengajuan"
            variant="delete"
            disabled={busy}
            onClick={() => handleDelete(row)}
          />
        </div>
      );
    }

    if (!canReview) return null;

    // Detail dulu, keputusan belakangan — tapi jalur cepat dari daftar tetap
    // ada supaya menilai banyak pengajuan tidak perlu buka-tutup halaman.
    //
    // Yang sudah DITOLAK tidak bisa diputuskan lagi: bolanya ada di pencipta.
    // Begitu ia merevisi, statusnya kembali `pending` (update_song) dan tombol
    // keputusannya muncul sendiri. Aturan yang sama dipakai di halaman detail
    // lewat prop `reviewActions`.
    const isDecidable = row.approval_status === PENDING;

    return (
      <div className="lagu-row-actions">
        <IconAction
          icon={FiEye}
          label="Lihat detail"
          variant="neutral"
          disabled={busy}
          onClick={() => handleViewDetail(row)}
        />
        {isDecidable && (
          <>
            <IconAction
              icon={FiCheck}
              label="Setujui lagu"
              variant="approve"
              disabled={busy}
              onClick={() => handleApprove(row)}
            />
            <IconAction
              icon={FiX}
              label="Tolak lagu"
              variant="decline"
              disabled={busy}
              onClick={() => handleDecline(row)}
            />
          </>
        )}
      </div>
    );
  };

  const columnRefs = [
    {
      dataField: "song_title",
      text: "Judul Lagu",
      headerStyle: columnLayout(1.6),
      style: columnLayout(1.6),
    },
    {
      dataField: isPencipta ? "creator_name" : "submitted_by_name",
      text: isPencipta ? "Songwriter" : "Diajukan Oleh",
      headerStyle: columnLayout(1.4),
      style: columnLayout(1.4),
    },
    {
      dataField: "created_at",
      text: "Tanggal Pengajuan",
      // `new Date(...)` dulu, bukan string ISO langsung: toDisplayDate memotong
      // bagian tanggal string apa adanya (UTC), sehingga pengajuan dini hari
      // WIB akan tampil sehari lebih awal. Objek Date dibaca pakai komponen
      // tanggal lokal.
      formatter: (cell) =>
        toDisplayDate(cell ? new Date(cell) : null) || "-",
      headerStyle: columnLayout(1.2, "center"),
      style: columnLayout(1.2, "center"),
    },
    {
      dataField: "approval_status",
      text: "Status",
      formatter: statusFormatter,
      headerStyle: columnLayout(1.5, "center"),
      style: columnLayout(1.5, "center"),
    },
    {
      dataField: "decline_note",
      text: "Catatan",
      formatter: noteFormatter,
      headerStyle: columnLayout(0.6, "center"),
      style: columnLayout(0.6, "center"),
    },
    ...(canReview || isPencipta
      ? [
          {
            dataField: "action",
            text: "Aksi",
            formatter: actionFormatter,
            headerStyle: actionColumnLayout(isPencipta ? 110 : 150),
            style: actionColumnLayout(isPencipta ? 110 : 150),
          },
        ]
      : []),
  ];

  // Pencipta mendaftarkan lagu memakai formulir yang sama dengan publisher.
  if (formMode === "tambahLagu") {
    return (
      <AddLagu
        handleOnClick={() => {
          setFormMode("");
          fetchRequests();
        }}
      />
    );
  }

  // Dua jalur ke halaman yang sama: pencipta merevisi lagunya yang ditolak,
  // publisher membaca detail sebelum memutuskan.
  if ((formMode === "revisiLagu" || formMode === "detailLagu") && detailData) {
    const isRevisi = formMode === "revisiLagu";

    return (
      <Detail
        data={detailData}
        rowData={detailRow}
        // Pencipta hanya sampai ke mode revisi untuk lagu yang DITOLAK (tombol
        // Revisi memang hanya muncul di baris itu), jadi ia boleh menyunting —
        // bawaan Detail melarang role 3 menyunting apa pun. Publisher yang
        // sedang menilai tidak menyunting; ia menyetujui atau menolak.
        canEdit={isRevisi && isPencipta}
        initialPage={isRevisi && isPencipta ? "edit" : "detail"}
        declineNote={detailRow?.decline_note || ""}
        // Susunan nama pada pengajuan pencipta memang tetap: publisher yang
        // menaunginya, lalu dirinya sendiri. Ditampilkan sebagai teks.
        lockNama={isPencipta}
        // Harga tidak diisi di tahap pengajuan, oleh siapa pun. Publisher
        // mengisinya setelah lagu disetujui, lewat Edit Lagu di Katalog Lagu.
        // Nilainya tetap terkirim apa adanya kalau form ini disimpan.
        hideHarga
        reviewActions={
          canReview && detailRow?.approval_status === PENDING
            ? {
                busy: reviewingId === detailRow?.song_id,
                onApprove: () => handleApprove(detailRow, closeForm),
                onDecline: () => handleDecline(detailRow, closeForm),
              }
            : null
        }
        // Ambil ulang daftarnya: revisi yang tersimpan sudah kembali pending.
        handleOnClick={closeForm}
      />
    );
  }

  return (
    <Container fluid className="katalog-lagu-container">
      <div className="header-content">
        <div className="katalog-header">
          <h1>Register Request</h1>
          <span>
            {isPencipta
              ? "Ajukan pendaftaran lagu baru pada halaman ini. Setiap pengajuan menunggu persetujuan publisher; pengajuan yang disetujui akan dipindahkan ke Katalog Lagu, sedangkan pengajuan yang ditolak dapat direvisi dan diajukan kembali."
              : "Daftar pengajuan pendaftaran lagu dari Songwriter di bawah naungan Anda. Periksa detail lagu terlebih dahulu, kemudian setujui untuk memasukkannya ke Katalog Lagu, atau tolak dengan disertai catatan agar dapat direvisi oleh Songwriter."}
          </span>
        </div>
        <div className="icon-wrapper">
          <Bell />
        </div>
      </div>

      <div className="tabs-filters">
        <div className="frame-filters">
          {isPencipta && (
            <CustomButton
              label="+ Daftarkan Lagu"
              color="primary-btn"
              size="md"
              className="button-add-anggota"
              onClick={() => setFormMode("tambahLagu")}
            />
          )}
        </div>
      </div>

      {isLoading ? (
        <TableLoading columnRefs={columnRefs} isShow={limit.value} />
      ) : (
        <Table
          columnRefs={columnRefs}
          dataTable={rows}
          noDataText={
            isPencipta
              ? "Belum ada pengajuan pendaftaran lagu"
              : "Belum ada pengajuan yang menunggu persetujuan"
          }
        />
      )}

      <div className="table-footer">
        <div className="table-footer-filters">
          <p className="text-show">Show :</p>
          <FormSelects
            options={SHOW_OPTIONS}
            value={limit.value}
            onChange={(opt) => {
              setLimit(opt);
              setPage(1);
            }}
            styles={TOOLBAR_SELECT_STYLES}
          />
        </div>
        <Pagination
          isTotalPage={Math.ceil(total / limit.value)}
          isPage={page}
          handleClickNumber={setPage}
          pageToShow={6}
        />
      </div>
    </Container>
  );
};

export default RegisterReq;
